import { Injectable } from '@angular/core';
import { IntegrationProvider } from '@/home/settings/integrations/integration-services/integration-provider';
import {
    ApiIntegrationCardDefinition,
    IntegrationCardData,
    IntegrationCardDefinition,
} from '../integration-card/integration-card.types';
import { IntegrationHelper } from '@/home/settings/integrations/integration-services/integration-helper';
import { TranslateService } from '@ngx-translate/core';
import { IntegrationConfig } from '@/core/models/IntegrationEntity';

@Injectable({ providedIn: 'root' })
export class IntegrationCardService {
    private readonly frontendIntegrations: IntegrationCardDefinition[];
    private readonly apiIntegrationExtensionsMap: Map<string, ApiIntegrationCardDefinition>;

    constructor(
        integrationProvider: IntegrationProvider,
        private translate: TranslateService,
    ) {
        this.frontendIntegrations = integrationProvider.getIntegrations();
        const extensions = integrationProvider.getApiIntegrationExtensions();
        this.apiIntegrationExtensionsMap = new Map<string, ApiIntegrationCardDefinition>();
        extensions.forEach((extension) => this.apiIntegrationExtensionsMap.set(extension.serviceId, extension));
    }

    public async getIntegrationCards(apiIntegrations: IntegrationConfig[]): Promise<IntegrationCardData[]> {
        const apiCards: IntegrationCardData[] = await Promise.all(
            apiIntegrations.map(async (integration): Promise<IntegrationCardData> => {
                const extension = this.apiIntegrationExtensionsMap.get(integration.service);
                const { name, searchText } = await IntegrationHelper.getCardTranslations(
                    this.translate,
                    integration.service,
                );
                return {
                    name,
                    serviceId: integration.service,
                    integration,
                    category: extension?.category ?? ['accounting'], // TODO is account as fallback a good idea?
                    devStatus: extension?.devStatus ?? 'stable',
                    searchText,
                    vendor: 'timeBuzzer',
                    extras: extension?.extras,
                };
            }),
        );
        const frontendCards = await this.getFrontendIntegrationCards();
        const allIntegrations = [...apiCards, ...frontendCards];
        allIntegrations.sort((a, b) => a.name.localeCompare(b.name));
        return allIntegrations;
    }

    private async getFrontendIntegrationCards(): Promise<IntegrationCardData[]> {
        return Promise.all(
            this.frontendIntegrations.map(async (integration): Promise<IntegrationCardData> => {
                const { name, searchText } = await IntegrationHelper.getCardTranslations(
                    this.translate,
                    integration.serviceId,
                );

                return {
                    ...integration,
                    searchText,
                    name,
                };
            }),
        );
    }
}
